import React, { useEffect, useRef, useState } from 'react';
import { LogOut, Plus, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FocusShell } from '../components/FocusShell';
import usePageTitle from '../hooks/usePageTitle';

interface NoOrganizationAccessProps {
  isRefreshing: boolean;
  onRefresh: () => Promise<void>;
  onSignOut: () => Promise<void>;
  onCreateOrganization: () => void;
}

const NoOrganizationAccess: React.FC<NoOrganizationAccessProps> = ({
  isRefreshing,
  onRefresh,
  onSignOut,
  onCreateOrganization,
}) => {
  usePageTitle('No Organization Access');
  /**
   * Set when a Refresh finished and this page is still here. A refresh that
   * finds a membership unmounts the page (Layout swaps in the app); one that
   * fails is the shell's error to show. So "still here" means "still none".
   */
  const [stillNone, setStillNone] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const handleRefresh = async () => {
    setStillNone(false);
    try {
      await onRefresh();
      if (mountedRef.current) setStillNone(true);
    } catch (err: unknown) {
      console.error('Refreshing organization access failed:', err);
    }
  };
  return (
    <FocusShell title="No organization access" width="xl">
      <Card>
        <CardContent className="space-y-6 pt-6">
          <p className="text-sm text-muted-foreground">
            Your account is authenticated, but it does not currently have access to any organizations.
            Please contact your system administrator to request organization access.
          </p>
          {/* Every action is a real way out, so none is removed; weight says
              which is the main next step. One primary, then outline, then ghost. */}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void handleRefresh()} loading={isRefreshing}>
              <RefreshCw aria-hidden="true" />
              Refresh
            </Button>
            <Button variant="outline" onClick={onCreateOrganization}>
              <Plus aria-hidden="true" />
              Create organization
            </Button>
            <Button variant="ghost" onClick={onSignOut}>
              <LogOut aria-hidden="true" />
              Sign out
            </Button>
          </div>
          <p role="status" className="text-sm text-muted-foreground">
            {stillNone && !isRefreshing ? 'Still no organization access.' : ''}
          </p>
        </CardContent>
      </Card>
    </FocusShell>
  );
};

export default NoOrganizationAccess;
